import { Component, HostListener, Input, OnInit } from '@angular/core';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { DialogAddIcalComponent } from '@app/modules/properties/components/dialog-add-ical/dialog-add-ical.component';
import { DialogDeleteGenericComponent } from '@app/shared/components/dialog-delete-generic/dialog-delete-generic.component';
import { NotificationType } from '@app/shared/interfaces';
import { ICalFeed, PropertiesService } from '@app/shared/services/properties/properties.service';
import { ToastNotificationsService } from '@app/shared/services/toast-notifications/toast-notifications.service';
import { EMPTY } from 'rxjs';
import { switchMap } from 'rxjs/operators';

@Component({
  selector: 'sbnb-property-detail-icals',
  templateUrl: './property-detail-icals.component.html',
  styleUrls: ['./property-detail-icals.component.scss'],
})
export class PropertyDetailIcalsComponent implements OnInit {
  @Input() property: any;
  icalFeeds: ICalFeed[] = [];
  loading = false;
  activeDropdown: string | null = null;

  constructor(
    private dialog: MatDialog,
    private propertyService: PropertiesService,
    private toast: ToastNotificationsService
  ) {}

  @HostListener('document:click', ['$event'])
  clickOutside(event: MouseEvent) {
    // Close dropdown when clicking outside unless it's a dropdown toggle button
    if (this.activeDropdown && !(event.target as HTMLElement).closest('.dropdown-toggle')) {
      this.activeDropdown = null;
    }
  }

  ngOnInit() {
    this.loadIcalFeeds();
  }

  loadIcalFeeds() {
    this.loading = true;
    this.propertyService.getPropertyIcals(this.property.id).subscribe(
      (response: { data: ICalFeed[] }) => {
        this.icalFeeds = response.data;
        this.loading = false;
      },
      (error) => {
        this.toast.open('Failed to load iCal feeds', 'Dismiss', NotificationType.Error);
        this.loading = false;
      }
    );
  }

  toggleDropdown(icalId: string) {
    if (this.activeDropdown === icalId) {
      this.activeDropdown = null;
    } else {
      this.activeDropdown = icalId;
    }
  }

  openAddIcalDialog() {
    const dialogRef = this.dialog.open(DialogAddIcalComponent, {
      maxWidth: '100vw',
      maxHeight: '100vh',
      height: '100%',
      width: '100%',
      data: { propertyId: this.property.id },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.loadIcalFeeds();
      }
    });

    // Close any open dropdown
    this.activeDropdown = null;
  }

  editIcalFeed(feed: ICalFeed) {
    const dialogRef = this.dialog.open(DialogAddIcalComponent, {
      maxWidth: '100vw',
      maxHeight: '100vh',
      height: '100%',
      width: '100%',
      data: {
        propertyId: this.property.id,
        icalFeed: feed
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.loadIcalFeeds();
      }
    });

    // Close the dropdown
    this.activeDropdown = null;
  }

  deleteIcalFeed(icalId: string) {
    const icalFeed = this.icalFeeds.find((feed) => feed.id === icalId);
    if (!icalFeed) return;

    const dialogRef = this.dialog.open(DialogDeleteGenericComponent, {
      data: {
        name: 'iCal feed',
        identifier: icalFeed.platform,
        additionalText: 'This will remove the iCal feed and stop syncing availability from this calendar.',
      },
    });

    dialogRef
      .afterClosed()
      .pipe(
        switchMap((result) => {
          if (result) {
            return this.propertyService.deletePropertyIcal(this.property.id, icalId);
          }
          return EMPTY;
        })
      )
      .subscribe(
        () => {
          this.toast.open('iCal feed deleted successfully', 'Dismiss', NotificationType.Success);
          this.loadIcalFeeds();
        },
        (error) => {
          this.toast.open('Failed to delete iCal feed', 'Dismiss', NotificationType.Error);
        }
      );

    // Close the dropdown
    this.activeDropdown = null;
  }
}
